import type { LastSentItemEntry, OrderItem } from '../types';

/**
 * Cents total for one weighed fish. Null when the inputs cannot make a positive price.
 * Grams × price-cents avoids `1.234 * 32.5 * 100` landing on 4010.499… and rounding down to $40.10.
 */
export function liveFishLineTotal(weightKg: number, pricePerKg: number): number | null {
  if (!Number.isFinite(weightKg) || !Number.isFinite(pricePerKg)) return null;
  if (weightKg <= 0 || pricePerKg <= 0) return null;
  const grams = Math.round(weightKg * 1000);
  const priceCents = Math.round(pricePerKg * 100);
  if (grams <= 0 || priceCents <= 0) return null;
  const totalCents = Math.round((grams * priceCents) / 1000);
  if (totalCents <= 0) return null;
  return totalCents / 100;
}

/** Parse a positive decimal with at most `maxDecimals` places. Empty or zero is null. */
export function parseLiveFishAmount(raw: string, maxDecimals: number): number | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const pattern = maxDecimals >= 3 ? /^\d+(\.\d{1,3})?$/ : /^\d+(\.\d{1,2})?$/;
  if (!pattern.test(trimmed)) return null;
  const value = Number(trimmed);
  if (!Number.isFinite(value) || value <= 0) return null;
  return value;
}

export function isWeighedFishLine(item: { soldByWeight?: boolean; weightKg?: number } | null | undefined): boolean {
  if (!item) return false;
  if (item.soldByWeight === true) return true;
  return typeof item.weightKg === 'number' && item.weightKg > 0;
}

/** Cart, receipt, and kitchen line: "Snapper · 1.250 kg @ $32.00/kg". */
export function formatLiveFishDetail(item: {
  selectedFish?: string;
  weightKg?: number;
  pricePerKg?: number;
} | null | undefined): string | null {
  const fish = item?.selectedFish?.trim();
  if (!fish) return null;
  if (typeof item?.weightKg !== 'number' || typeof item?.pricePerKg !== 'number') return null;
  if (!Number.isFinite(item.weightKg) || !Number.isFinite(item.pricePerKg)) return null;
  return `${fish} · ${item.weightKg.toFixed(3)} kg @ $${item.pricePerKg.toFixed(2)}/kg`;
}

export function liveFishSnapshotFields(item: Pick<OrderItem, 'selectedFish' | 'weightKg' | 'pricePerKg'>): Pick<
  LastSentItemEntry,
  'selectedFish' | 'weightKg' | 'pricePerKg'
> {
  const fields: Pick<LastSentItemEntry, 'selectedFish' | 'weightKg' | 'pricePerKg'> = {};
  const fish = item.selectedFish?.trim();
  if (fish) fields.selectedFish = fish;
  if (typeof item.weightKg === 'number' && Number.isFinite(item.weightKg)) fields.weightKg = item.weightKg;
  if (typeof item.pricePerKg === 'number' && Number.isFinite(item.pricePerKg)) fields.pricePerKg = item.pricePerKg;
  return fields;
}
